/**
 * Centralized tool name registry.
 *
 * All tools MUST import their name from here and tool
 * name MUST be globally unique.
 */
import { z } from "zod";

export const TOOL_NAMES = {
  // View and search tools
  FIND_TABLE_SCHEMAS: "FindTableSchemas",
  FIND_TABLE_SCHEMAS_SEMANTIC_AUTHORING: "FindTableSchemasSemanticAuthoring",
  FIND_TAO_PROJECT_SEMANTIC_COLUMNS: "FindTaoProjectSemanticColumns",
  SEARCH: "Search",
  INSPECT: "Inspect",
  FIND_TAO_PROJECT_TABLE_SCHEMAS: "FindTaoProjectTableSchemas",
  INSPECT_TAO_PROJECT_SEMANTIC_DATA: "InspectTaoProjectSemanticData",
  LIST_TAO_PROJECT_SEMANTIC_VIEWS: "ListTaoProjectSemanticViews",
  VIEW_TABLES: "ViewTables",
  VIEW_TAO_PROJECT_TABLES: "ViewTaoProjectTables",
  LINK_TAO_PROJECT_DATA_CONNECTION: "LinkTaoProjectDataConnection",
  LIST_TAO_PROJECT_DATA_CONNECTIONS: "ListTaoProjectDataConnections",
  RUN_SQL: "RunSQL",
  GET_KERNEL_PACKAGES_INFO: "GetKernelPackagesInfo",
  SEARCH_HEX_DOCS: "SearchHexDocs",
  SEARCH_WEB: "SearchWeb",
  EXTRACT_WEB: "ExtractWeb",
  LOOK_UP_PROJECT: "LookUpProject",
  SEARCH_TOOLS: "SearchTools",
  GET_CURRENT_DATE_TIME: "GetCurrentDateTime",
  SEARCH_KNOWLEDGE: "SearchKnowledge",

  // Curation agent tools
  INSPECT_KNOWLEDGE_ATOMS: "InspectKnowledgeAtoms",
  LIST_ASSUMPTION_FRONTIERS: "ListAssumptionFrontiers",
  MARK_VERSIONED_CONTEXT_RESOURCE_RESOLVED:
    "MarkVersionedContextResourceResolved",
  SEARCH_RAW_ASSUMPTIONS: "SearchRawAssumptions",
  SEARCH_RESOURCE_REFERENCE: "SearchResourceReference",
  UPSERT_KNOWLEDGE_ATOM: "UpsertKnowledgeAtom",
  RESOLVE_ASSUMPTION: "ResolveAssumption",
  READ_VERSIONED_CONTEXT_RESOURCE: "ReadVersionedContextResource",

  // Ask/Threads-specific tool for looking at **another** project
  GET_FILE_CONTENTS: "GetFileContents",

  // Lists the project's uploaded/cell-written files (Files panel) with their ids
  LIST_PROJECT_FILES: "ListProjectFiles",

  // Org guide tools
  READ_GUIDE: "ReadGuide",
  SEARCH_GUIDES: "SearchGuides",

  // Slack thread context tool
  FETCH_SLACK_THREAD_MESSAGES: "FetchSlackThreadMessages",

  // MCP tools
  LIST_MCP_TOOLS: "ListMcpTools",
  CALL_MCP_TOOL: "CallMcpTool",

  // Run cells tools
  RUN_APP: "RunApp",
  WAIT_FOR_CELL: "WaitForCell",
  STOP_CELL_RUN: "StopCellRun",

  EXPLORE_PROJECTS: "ExploreProjects",
  FIND_SEMANTIC_COLUMNS: "FindSemanticColumns",
  INSPECT_SEMANTIC_DATA: "InspectSemanticData",
  CELL_SEARCH: "CellSearch",

  // Create cell tools
  CREATE_MARKDOWN_CELL: "CreateMarkdownCell",
  CREATE_SECTION: "CreateSection",
  CREATE_SQL_CELL: "CreateSqlCell",
  CREATE_PYTHON_CELL: "CreatePythonCell",
  CREATE_PIVOT_CELL: "CreatePivotCell",
  CREATE_METRIC_CELL: "CreateMetricCell",
  CREATE_INPUT_CELL: "CreateInputCell",
  CREATE_SANKEY_CELL: "CreateSankeyCell",

  // Edit cell tools
  EDIT_REPLACE_MARKDOWN_CELL: "EditReplaceMarkdownCell",
  EDIT_REPLACE_SQL_CELL: "EditReplaceSqlCell",
  EDIT_REPLACE_PYTHON_CELL: "EditReplacePythonCell",
  ADD_EDIT_CHART_CELL: "AddEditChartCell",
  RENAME_CELL_LABEL: "RenameCellLabel",
  RENAME_CELL_OUTPUT: "RenameCellOutput",

  // Diff edit tools
  EDIT_MARKDOWN_CELL_DIFF: "EditMarkdownCellDiff",
  EDIT_SQL_CELL_DIFF: "EditSqlCellDiff",
  EDIT_PYTHON_CELL_DIFF: "EditPythonCellDiff",
  EDIT_PIVOT_CELL: "EditPivotCell",
  EDIT_METRIC_CELL: "EditMetricCell",
  EDIT_INPUT_CELL: "EditInputCell",
  EDIT_SQL_CELL_DATA_CONNECTION: "EditSqlCellDataConnection",

  // Vis tools
  STYLE_CHART_CELL: "StyleChartCell",
  ADD_EDIT_DELETE_CHART_CELL_SERIES: "AddEditDeleteChartCellSeries",
  ADD_EDIT_DELETE_REFERENCE_LINE_ON_CHART_CELL:
    "AddEditDeleteReferenceLineOnChartCell",

  // Vis cell conversion tools
  UPGRADE_LEGACY_CHART_CELL: "UpgradeLegacyChartCell",
  DUPLICATE_LEGACY_PIVOT_CELL: "DuplicateLegacyPivotCell",

  // Other cell tools
  MOVE_CELLS: "MoveCells",
  DELETE_CELLS: "DeleteCells",
  SET_CELLS_IN_APP: "SetCellsInApp",

  // App layout tools
  VIEW_APP_LAYOUT: "ViewAppLayout",
  SET_APP_LAYOUT: "SetAppLayout",
  SET_TAB_LAYOUT: "SetTabLayout",
  CREATE_TAB_LAYOUT: "CreateTabLayout",
  DELETE_TAB_LAYOUT: "DeleteTabLayout",
  PUBLISH_APP: "PublishApp",
  UPDATE_CELL_DISPLAY_SETTINGS: "UpdateCellDisplaySettings",
  SET_APP_LAYOUT_TYPE: "SetAppLayoutType",

  // Other tools
  CREATE_SEMANTIC_TABLE: "CreateSemanticTable",
  LIST_SEMANTIC_VIEWS: "ListSemanticViews",

  // Semantic authoring tools
  BOOTSTRAP_SEMANTIC_DATA_MODEL_FROM_TABLE:
    "BootstrapSemanticDataModelFromTable",
  CREATE_SEMANTIC_PROJECT_FILE: "CreateSemanticProjectFile",
  EDIT_SEMANTIC_PROJECT_FILE: "EditSemanticProjectFile",
  RENAME_SEMANTIC_PROJECT_FILE: "RenameSemanticProjectFile",
  VALIDATE_SEMANTIC_PROJECT: "ValidateSemanticProject",
  LIST_SEMANTIC_PROJECT_FILES: "ListSemanticProjectFiles",
  VIEW_SEMANTIC_PROJECT_FILES: "ViewSemanticProjectFiles",

  // Review Agent tools
  REVIEW_READ_GUIDE: "ReadOrgGuide",
  REVIEW_READ_RULES: "ReadWorkspaceRules",
  REVIEW_READ_METADATA: "ReadMetadata",
  LIST_CONTEXT_SUGGESTIONS: "ListContextSuggestions",
  INSPECT_CONTEXT_SUGGESTION: "InspectContextSuggestion",
  INSPECT_WORKSPACE_THREADS: "InspectWorkspaceThreads",
  REVIEW_READ_THREAD_MESSAGES: "ReadThreadMessages",
  REVIEW_READ_SEMANTIC_PROJECT_FILE: "ReadSemanticProjectFile",
  REVIEW_FIND_SEMANTIC_DATASETS: "FindSemanticDatasets",
  REVIEW_PROPOSE_CREATE_ORG_GUIDE: "ProposeCreateOrgGuide",
  REVIEW_PROPOSE_UPDATE_ORG_GUIDE: "ProposeUpdateOrgGuide",
  REVIEW_PROPOSE_UPDATE_WORKSPACE_RULES: "ProposeUpdateWorkspaceRules",
  REVIEW_PROPOSE_UPDATE_METADATA: "ProposeUpdateMetadata",
  REVIEW_PROPOSE_ENDORSE_RESOURCE: "ProposeEndorseResource",
  REVIEW_PROPOSE_CREATE_SEMANTIC_PROJECT_FILE:
    "ProposeCreateSemanticProjectFile",
  REVIEW_PROPOSE_UPDATE_SEMANTIC_PROJECT_FILE:
    "ProposeUpdateSemanticProjectFile",
  REVIEW_PROPOSE_RENAME_SEMANTIC_PROJECT_FILE:
    "ProposeRenameSemanticProjectFile",
  REVIEW_PROPOSE_SEMANTIC_WORKBENCH_TASK: "ProposeSemanticWorkbenchTask",

  // Data discovery tools
  DELEGATE_TO_DATA_DISCOVERY_AGENT: "DelegateToDataDiscoveryAgent", // Tool call to delegate search to a data discovery subagent.
  LIST_DATA_CONNECTIONS: "ListDataConnections",

  // Visualization subagent tools
  DELEGATE_TO_VISUALIZATION_AGENT: "DelegateToVisualizationAgent", // Tool call to delegate visualization work to a visualization subagent.

  // Memory tools
  READ_PAST_THREAD_SUMMARY: "ReadPastThreadSummary",
  SAVE_MEMORY: "SaveMemory",

  // Thread artifact tools
  UPSERT_RESOURCE: "UpsertResource",
  READ_ARTIFACT_STATE: "ReadArtifactState",
  UPSERT_ASSUMPTION: "UpsertAssumption",
  DELETE_ARTIFACT: "DeleteArtifact",
  UPSERT_THREAD_SUMMARY: "UpsertThreadSummary",
  SET_THREAD_CLASSIFICATION: "SetThreadClassification",
  CREATE_THREAD_CONFUSION: "CreateThreadConfusion",

  // Hero tools
  INSERT_HERO_FINDING: "InsertHeroFinding",
  SUBMIT_HERO_CLUSTER_PROPOSAL: "SubmitHeroClusterProposal",

  // GitHub repo tools
  GITHUB_REPO_DOWNLOAD: "GithubRepoDownload",
  REPO_SHELL_EXEC: "RepoShellExec",
  TAO_GITHUB_REPO_DOWNLOAD: "TaoGithubRepoDownload",

  // Agent desk tools
  SHELL_EXEC: "ShellExec",

  // Chat with app tools
  SEARCH_VALID_INPUT_VALUES: "SearchValidInputValues",
  SET_APP_CELL_FILTERS_AND_SORT: "SetAppCellFiltersAndSort",
  SET_APP_INPUTS: "SetAppInputs",
  VIEW_DEFINITION: "ViewDefinition",

  // Data app tools
  DELEGATE_TO_DATA_APP_AGENT: "DelegateToDataAppAgent",
  READ_DATA_APP_FILE: "ReadDataAppFile",
  WRITE_DATA_APP_FILE: "WriteDataAppFile",
  EDIT_DATA_APP_FILE: "EditDataAppFile",
  EDIT_DATA_APP_FILE_DIFF: "EditDataAppFileDiff",
  LIST_DATA_APP_FILES: "ListDataAppFiles",
  DELETE_DATA_APP_FILE: "DeleteDataAppFile",
  COPY_DATA_APP_FILES: "CopyDataAppFiles",
  MOVE_DATA_APP_FILES: "MoveDataAppFiles",
  GREP_DATA_APP_FILES: "GrepDataAppFiles",
  READ_DATA_APP_CONSOLE: "ReadDataAppConsole",
  READ_DATA_APP_SCREENSHOT: "ReadDataAppScreenshot",

  // Plain code project (repo) tools
  LIST_PROJECTS: "ListProjects",
  LIST_TAO_PROJECT_FILES: "ListTaoProjectFiles",
  READ_TAO_PROJECT_FILE: "ReadTaoProjectFile",
  WRITE_TAO_PROJECT_FILE: "WriteTaoProjectFile",
  EDIT_TAO_PROJECT_FILE: "EditTaoProjectFile",
  EDIT_TAO_PROJECT_FILE_DIFF: "EditTaoProjectFileDiff",
  GREP_TAO_PROJECT_FILES: "GrepTaoProjectFiles",
  DELETE_TAO_PROJECT_FILES: "DeleteTaoProjectFiles",
  READ_DAG_STRUCTURE: "ReadDagStructure",
  READ_DAG_NOTEBOOK: "ReadDagNotebook",
  RUN_DAG_RESULTS: "RunDagResults",
  RUN_EPHEMERAL_SQL: "RunEphemeralSql",
  RUN_DAG_SHELL_EXEC: "RunDagShellExec",
  BUILD_TAO_PROJECT_APP: "BuildTaoProjectApp",
  LIST_TAO_PROJECT_COMPONENTS: "ListTaoProjectComponents",
  CREATE_TAO_CHART: "CreateChart",
  CREATE_TAO_DISPLAY_TABLE: "CreateDisplayTable",
  CREATE_TAO_KPI_CARD: "CreateKPICard",
  CREATE_TAO_INPUT: "CreateInput",
  ADD_TAO_PROJECT_COMPONENT: "AddTaoProjectComponent",
  SCAFFOLD_TAO_PROJECT_APP: "ScaffoldTaoProjectApp",
  RESTART_DAG_EXECUTORS: "RestartDagExecutors",
  COPY_TAO_PROJECT_FILES: "CopyTaoProjectFiles",
  READ_TAO_PROJECT_CONSOLE: "ReadTaoProjectConsole",
  READ_TAO_PROJECT_SCREENSHOT: "ReadTaoProjectScreenshot",
  READ_TAO_APP_SCREENSHOT: "ReadTaoAppScreenshot",

  // Project metadata tools
  EDIT_PROJECT_METADATA: "EditProjectMetadata",

  // Legacy tool names kept for historical data parsing
  OUTLINE_PROJECT: "OutlineProject",
  RELOAD_DAG_PROJECT: "ReloadDagProject",

  // Review suggestion tools
  SUGGEST_REVIEW: "SuggestReview",

  // Clarification tools
  ASK_USER_QUESTION: "AskUserQuestion",

  // Task tools
  CREATE_TASK: "CreateTask",
  PREPARE_EVAL: "PrepareEval",
  VIEW_TASK: "ViewTask",
  // Notification tools
  SEND_NOTIFICATION: "SendNotification",
  // Subagent utility tools
  SUMMARIZE_SUBAGENT_WORK: "SummarizeSubagentWork",
} as const;

/**
 * Tool names that delegate to subagents. Used to identify delegation
 * tool calls/results for UI filtering and block segmentation.
 */
export const SUBAGENT_DELEGATION_TOOL_NAMES: ReadonlySet<string> = new Set([
  TOOL_NAMES.DELEGATE_TO_DATA_APP_AGENT,
  TOOL_NAMES.DELEGATE_TO_DATA_DISCOVERY_AGENT,
  TOOL_NAMES.DELEGATE_TO_VISUALIZATION_AGENT,
]);

/**
 * Tool calls/results for these tools are hidden from the UI entirely.
 * A thinking block containing only messages from these tools should not
 * be shown to the user (it would appear as an empty collapsible block).
 */
export const UI_HIDDEN_TOOL_NAMES: ReadonlySet<string> = new Set([
  ...SUBAGENT_DELEGATION_TOOL_NAMES,
  TOOL_NAMES.SEARCH_TOOLS,
  TOOL_NAMES.LIST_MCP_TOOLS,
  TOOL_NAMES.UPSERT_RESOURCE,
  TOOL_NAMES.READ_ARTIFACT_STATE,
  TOOL_NAMES.UPSERT_ASSUMPTION,
  TOOL_NAMES.UPSERT_KNOWLEDGE_ATOM,
  TOOL_NAMES.RESOLVE_ASSUMPTION,
  TOOL_NAMES.MARK_VERSIONED_CONTEXT_RESOURCE_RESOLVED,
  TOOL_NAMES.DELETE_ARTIFACT,
  TOOL_NAMES.UPSERT_THREAD_SUMMARY,
  TOOL_NAMES.SET_THREAD_CLASSIFICATION,
  TOOL_NAMES.CREATE_THREAD_CONFUSION,
]);

/**
 * Confirm ask-user prompts for these tools do not offer "don't ask again in this thread",
 * and confirming does not persist per-thread ask-user auto-approvals for the tool.
 */
const TOOL_NAMES_EXCLUDED_FROM_AUTO_APPROVE: ReadonlySet<string> = new Set([
  TOOL_NAMES.CREATE_TASK,
  TOOL_NAMES.PREPARE_EVAL,
]);

export function isToolExcludedFromThreadAutoApprove(toolName: string): boolean {
  return TOOL_NAMES_EXCLUDED_FROM_AUTO_APPROVE.has(toolName);
}

type ToolNameValue = (typeof TOOL_NAMES)[keyof typeof TOOL_NAMES];

const TOOL_NAME_LITERALS = Object.values(TOOL_NAMES).map((name) =>
  z.literal(name),
) as [
  z.ZodLiteral<ToolNameValue>,
  z.ZodLiteral<ToolNameValue>,
  ...z.ZodLiteral<ToolNameValue>[],
];

export const ToolNameLiteral = z.union(TOOL_NAME_LITERALS);
export type ToolName = z.infer<typeof ToolNameLiteral>;
